/**
 * Painel lateral de ferramentas para a aba de transcrição.
 * Exibe uma barra vertical de ícones (toolbar) com espaço para ferramentas futuras.
 * Ao clicar em um ícone, abre o painel com o conteúdo da ferramenta selecionada.
 */

import React from 'react'
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'

export interface ToolDefinition {
  id: string
  icon: React.ReactNode
  label: string
  tooltip: string
  disabled?: boolean
}

interface ToolsSidePanelProps {
  tools: ToolDefinition[]
  activeTool: string | null
  onToolChange: (toolId: string | null) => void
  children: React.ReactNode
  panelWidth?: number
}

export default function ToolsSidePanel({
  tools,
  activeTool,
  onToolChange,
  children,
  panelWidth = 350,
}: ToolsSidePanelProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const activeLabel = tools.find(t => t.id === activeTool)?.label || ''

  const handleToggle = (toolId: string) => {
    onToolChange(activeTool === toolId ? null : toolId)
  }

  // Barra vertical de ícones das ferramentas
  const toolbar = (
    <Box
      sx={{
        width: 44,
        minWidth: 44,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 1,
        gap: 0.5,
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fafafa',
      }}
    >
      {tools.map((tool) => (
        <Tooltip key={tool.id} title={tool.tooltip} placement="left" arrow>
          <span>
            <IconButton
              size="small"
              onClick={() => !tool.disabled && handleToggle(tool.id)}
              disabled={tool.disabled}
              sx={{
                width: 36,
                height: 36,
                color: activeTool === tool.id ? 'primary.main' : 'text.secondary',
                bgcolor: activeTool === tool.id ? 'primary.50' : 'transparent',
                border: activeTool === tool.id ? '1.5px solid' : '1px solid transparent',
                borderColor: activeTool === tool.id ? 'primary.main' : 'transparent',
                '&:hover': {
                  bgcolor: activeTool === tool.id ? 'primary.50' : 'action.hover',
                },
                transition: 'all 0.2s',
              }}
            >
              {tool.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}

      {/* Indicador de mais ferramentas em breve */}
      <Divider sx={{ width: '60%', my: 0.5 }} />
      <Tooltip title="Mais ferramentas em breve" placement="left" arrow>
        <span>
          <IconButton size="small" disabled sx={{ width: 36, height: 36, opacity: 0.3 }}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )

  // Conteúdo do painel
  const needsOwnScroll = activeTool === 'chat'

  const panelContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        '#monkee-file-transcript-client &': {
          height: '100% !important',
          overflow: 'hidden !important',
          display: 'flex !important',
          flexDirection: 'column !important',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fafafa',
          minHeight: 48,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} noWrap>
          {activeLabel}
        </Typography>
        <IconButton size="small" onClick={() => onToolChange(null)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: needsOwnScroll ? 'hidden' : 'auto',
          p: needsOwnScroll ? 0 : 1.5,
          '#monkee-file-transcript-client &': {
            flex: '1 1 0 !important',
            minHeight: '0 !important',
            overflow: needsOwnScroll ? 'hidden !important' : 'auto !important',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  )

  // Mobile: Drawer temporário
  if (isMobile) {
    return (
      <>
        {toolbar}
        <Drawer
          anchor="right"
          open={activeTool !== null}
          onClose={() => onToolChange(null)}
          PaperProps={{ sx: { width: '85vw', maxWidth: 400 } }}
        >
          {panelContent}
        </Drawer>
      </>
    )
  }

  // Desktop: Painel flutuante fixo no lado direito
  // Em WordPress, a barra de admin tem position:fixed top:0 com z-index 99999 e 32px de altura,
  // cobrindo o primeiro ícone do toolbar. Por isso deslocamos top para 32px no contexto WordPress.
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        height: '100vh',
        display: 'flex',
        zIndex: 1200,
        '#monkee-file-transcript-client &': {
          top: '32px !important',
          height: 'calc(100vh - 32px) !important',
        },
      }}
    >
      <Collapse in={activeTool !== null} orientation="horizontal" timeout={250} unmountOnExit>
        <Box
          sx={{
            width: panelWidth,
            minWidth: panelWidth,
            height: '100vh',
            borderLeft: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
            boxShadow: '-4px 0 12px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column',
            '#monkee-file-transcript-client &': {
              height: 'calc(100vh - 32px) !important',
              display: 'flex !important',
              flexDirection: 'column !important',
              overflow: 'hidden !important',
            },
          }}
        >
          {panelContent}
        </Box>
      </Collapse>
      {toolbar}
    </Box>
  )
}
